// Recepción — sessions list (/po/scan). Standalone top-level page (not
// nested under a layout's <Outlet> — mirrors Caja.tsx/Movil.tsx: fetch the
// user itself, render its own Header). Reached from both Compras
// (PurchasingLayout) and Móvil (Movil.tsx).
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  Badge, Button, Card, Select, SelectItem,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { deleteReception, listReceptions } from '../api/reception';
import { getStores } from '../api/priceUpload';
import AccessDenied from '../components/AccessDenied';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import Header from '../components/Header';
import TableSkeleton from '../components/TableSkeleton';
import { receptionStatusColor, receptionStatusLabel } from '../lib/format';
import { canUseReceiving } from '../lib/roles';
import { t } from '../lib/i18n';
import type { ReceptionSession, Store, User } from '../api/types';

type StatusKey = keyof typeof t.recepcion.status;
const STATUSES = Object.keys(t.recepcion.status) as StatusKey[];

export default function Reception() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {canUseReceiving(user) ? <ReceptionInner /> : <AccessDenied user={user} />}
      </main>
    </div>
  );
}

function ReceptionInner() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ReceptionSession[] | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | StatusKey>('all');
  const [storeFilter, setStoreFilter] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ReceptionSession | null>(null);

  const load = () => {
    setErr(null);
    setSessions(null);
    listReceptions()
      .then(setSessions)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    getStores().then(setStores).catch(() => { /* store names are a nicety — sid fallback below */ });
  }, []);

  const storeName = (sid: number) => stores.find((s) => s.sid === sid)?.name ?? `#${sid}`;

  const storeSids = useMemo(
    () => Array.from(new Set((sessions ?? []).map((s) => s.sid))).sort((a, b) => a - b),
    [sessions],
  );

  const rows = useMemo(() => {
    let pool = sessions ?? [];
    if (statusFilter !== 'all') pool = pool.filter((s) => s.status === statusFilter);
    if (storeFilter !== null) pool = pool.filter((s) => s.sid === storeFilter);
    return [...pool].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  }, [sessions, statusFilter, storeFilter]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await deleteReception(target.id);
      load();
    } catch (e) {
      setErr((e as Error).message ?? t.recepcion.errors.generic);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Title>{t.recepcion.title}</Title>
          <Text className="text-tremor-content text-sm">{t.recepcion.subtitle}</Text>
        </div>
        <Link to="/po/scan/nuevo">
          <Button>+ {t.recepcion.newSession}</Button>
        </Link>
      </div>

      {sessions !== null && sessions.length > 0 && (
        <Card className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.recepcion.filters.status}</div>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as 'all' | StatusKey)}
              enableClear={false}
              className="w-44"
            >
              <SelectItem value="all">{t.recepcion.filters.statusAll}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{t.recepcion.status[s]}</SelectItem>
              ))}
            </Select>
          </label>
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.recepcion.filters.store}</div>
            <Select
              value={storeFilter === null ? 'all' : String(storeFilter)}
              onValueChange={(v) => setStoreFilter(v === 'all' ? null : Number(v))}
              enableClear={false}
              className="w-52"
            >
              <SelectItem value="all">{t.recepcion.filters.storeAll}</SelectItem>
              {storeSids.map((sid) => (
                <SelectItem key={sid} value={String(sid)}>{storeName(sid)}</SelectItem>
              ))}
            </Select>
          </label>
        </Card>
      )}

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && sessions === null && <TableSkeleton rows={6} cols={6} />}
      {!err && sessions !== null && rows.length === 0 && <EmptyState message={t.recepcion.empty} />}
      {!err && rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.recepcion.cols.onum}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.cols.supplier}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.cols.store}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.cols.status}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.cols.createdBy}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.cols.createdAt}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.cols.actions}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((s) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer hover:bg-tremor-background-muted"
                    onClick={() => navigate(`/po/scan/${s.id}`)}
                  >
                    <TableCell className="font-medium">#{s.onum}</TableCell>
                    <TableCell>{s.sup_name || s.sup_id || '—'}</TableCell>
                    <TableCell>{storeName(s.sid)}</TableCell>
                    <TableCell>
                      <Badge color={receptionStatusColor(s.status)}>{receptionStatusLabel(s.status)}</Badge>
                    </TableCell>
                    <TableCell>{s.created_by || '—'}</TableCell>
                    <TableCell className="text-sm">
                      {s.created_at ? new Date(s.created_at).toLocaleString('es-MX') : '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="xs"
                        variant="secondary"
                        color="red"
                        onClick={(e) => { e.stopPropagation(); setPendingDelete(s); }}
                      >
                        {t.recepcion.deleteConfirm.confirm}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t.recepcion.deleteConfirm.title}
          message={t.recepcion.deleteConfirm.message.replace('{onum}', String(pendingDelete.onum))}
          confirmLabel={t.recepcion.deleteConfirm.confirm}
          cancelLabel={t.recepcion.deleteConfirm.cancel}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
